import { CommonSuccessResponse } from "constants/types/common";
import { PartnerParamsType, PartnerResponseType, PartnerType } from "constants/types/partner";
import { useMemo } from "react";
import useSWR from "swr";
import { serialize } from "utils/validation/string";

type PartnerListData = CommonSuccessResponse<PartnerResponseType[]> | undefined;

const toOptions = (items?: PartnerResponseType[]) =>
  items?.map((item) => ({
    label: `${item.name} (${item.code})`,
    value: item.id,
  }));

export const getPartnerListKey = (params?: PartnerParamsType) =>
  `/partner?${serialize({ ...params })}`;

export const getPartnerAllKey = (type?: PartnerType) =>
  `/partner/all?${serialize({ type })}`;

export const usePartners = (params?: PartnerParamsType) => {
  const { data, error, isLoading, mutate } = useSWR<PartnerListData>(
    getPartnerListKey(params),
    { refreshInterval: 0 }
  );

  return {
    partners: data?.data,
    pagination: data?.meta,
    error,
    isLoading,
    mutate,
  };
};

export const useCustomers = () => {
  const { data, error, isLoading, mutate } = useSWR<PartnerListData>(
    getPartnerAllKey("customer"),
    { refreshInterval: 0 }
  );

  const customerOptions = useMemo(() => toOptions(data?.data), [data?.data]);

  return {
    customerOptions,
    error,
    isLoading,
    mutate,
  };
};

export const useSuppliers = () => {
  const { data, error, isLoading, mutate } = useSWR<PartnerListData>(
    getPartnerAllKey("supplier"),
    { refreshInterval: 0 }
  );

  const supplierOptions = useMemo(() => toOptions(data?.data), [data?.data]);

  return {
    supplierOptions,
    error,
    isLoading,
    mutate,
  };
};

/**
 * CẢNH BÁO: lọc `type=both` nghĩa là CHỈ lấy đối tác được gắn nhãn "both",
 * KHÔNG phải "lấy tất cả". Khách hàng thuần (type=customer) và nhà cung cấp
 * thuần (type=supplier) đều bị loại khỏi kết quả.
 * Cần danh sách để chọn đối tác thì dùng useCustomers() hoặc useSuppliers().
 */
export const useListPartner = () => {
  const { data, error, isLoading, mutate } = useSWR<PartnerListData>(
    getPartnerAllKey("both"),
    { refreshInterval: 0 }
  );

  const partnerOptions = useMemo(() => toOptions(data?.data), [data?.data]);

  return {
    partners: data?.data,
    partnerOptions,
    error,
    isLoading,
    mutate,
  };
};
